/**
 * DeviceListSidebar - Clean rebuild of the left panel device list
 * Focused, simple implementation with stable scroll
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Badge,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Fingerprint as DeviceIcon,
  PlayArrow as LoadIcon,
  Star as StarIcon,
  Note as NoteIcon,
  Visibility,
  VisibilityOff,
  CenterFocusStrong as ZoomExtentIcon,
  Public as CountryIcon,
  PlaylistPlay as LoadAllIcon,
  PersonPinCircle as FocusIcon,
  Download as DownloadIcon,
  Radar as TracerIcon,
  Delete as DeleteIcon,
  Flag as FlagIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { generateDeviceColor } from '../../theme/entityColors';
import AskTracer from './AskTracer';
import OverwatchIntel from './OverwatchIntel';
import { PathfinderDataPoint } from '../../services/pathfinderApi';
import { OverwatchArticle } from '../../services/overwatchApi';
import { Newspaper as OverwatchIcon } from '@mui/icons-material';

// Geographic bounds type for map viewport filtering
export interface MapBounds {
  north: number; // Max latitude
  south: number; // Min latitude
  east: number;  // Max longitude
  west: number;  // Min longitude
}

// Types
interface GeoNeutronDevice {
  registration_id: string;
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
  source?: string;
  country?: string;
  carrier?: string;
  timezone?: string;
  forensic_flag?: string;
  all_forensic_flags?: string[];
  deviceType?: string | null;
  ip_address?: string | null;
  browser_family?: string | null;
  browser_version?: string | null;
  os_family?: string | null;
  os_version?: string | null;
  device_brand?: string | null;
  device_family?: string | null;
  device_model?: string | null;
  advertiser_eu?: boolean | null;
  user_agent_full?: string | null;
  created_on?: string | null;
  updated_on?: string | null;
  metadata?: Record<string, any>;
}

interface DeviceGroup {
  registration_id: string;
  pointCount: number;
  lastPoint: GeoNeutronDevice;
  latestTimestamp: string;
  allForensicFlags: Set<string>;
}

export interface AnalysisTab {
  id: string;
  label: string;
  data: GeoNeutronDevice[];
  totalDevices: number;
  type?: 'area' | 'pol' | 'query' | 'csv';
  uploadedFiles?: string[]; // For Uploaded Data tab - list of uploaded file names
  alwaysVisible?: boolean; // If true, this tab's data is always visible on map
  polDataPoints?: any[];
  areaDataPoints?: any[];
}

interface AskTracerProps {
  dataPoints: PathfinderDataPoint[];
  dwellLocations: any[];
  onZoomToLocation?: (lat: number, lng: number, zoom?: number) => void;
}

interface DeviceListSidebarProps {
  tabs: AnalysisTab[];
  activeTabId?: string;
  onTabChange?: (tabId: string) => void;
  onTabClose?: (tabId: string) => void;
  onZoomToDeviceExtent?: (registrationId: string, devicePoints: GeoNeutronDevice[]) => void;
  onLoadDevice?: (registrationId: string) => void;
  onLoadAllDevices?: (registrationIds: string[]) => void;
  onSaveDevice?: (registrationId: string) => void;
  onDeviceNotes?: (registrationId: string) => void;
  onToggleDeviceVisibility?: (registrationId: string) => void;
  onFocusDevice?: (registrationId: string, allDeviceIds: string[]) => void;
  hiddenDevices: Set<string>;
  savedDeviceIds: Set<string>;
  savedDeviceNames?: Map<string, string>;
  // Filter by map bounds props
  filterByMapBounds?: boolean;
  onFilterByMapBoundsChange?: (enabled: boolean) => void;
  mapBounds?: MapBounds | null;
  selectedCountries?: string[];
  selectedCarriers?: string[];
  selectedTimezones?: string[];
  selectedForensicFlags?: string[];
  onCountriesChange?: (countries: string[]) => void;
  onCarriersChange?: (carriers: string[]) => void;
  onTimezonesChange?: (timezones: string[]) => void;
  onForensicFlagsChange?: (flags: string[]) => void;
  // Ask Tracer integration
  showAskTracer?: boolean;
  askTracerProps?: AskTracerProps;
  // Overwatch Intel integration
  showOverwatch?: boolean;
  overwatchDataPoints?: PathfinderDataPoint[];
  overwatchDwellLocations?: Array<{ latitude: number; longitude: number; [key: string]: unknown }>;
  onLoadArticlesToMap?: (articles: OverwatchArticle[]) => void;
  loadedArticles?: OverwatchArticle[];
  onClearArticles?: () => void;
  onZoomToLocation?: (lat: number, lng: number, zoom?: number) => void;
  // Uploaded data management
  onDeleteUploadedPoint?: (pointId: string) => void;
  onClearAllUploadedData?: () => void;
}

// Helpers
const formatTimestamp = (ts: string) => {
  try {
    return new Date(ts).toLocaleString('en-US', { timeZone: 'UTC', hour12: false }) + ' UTC';
  } catch {
    return ts;
  }
};

const formatTimestampCSV = (ts: string | Date) => {
  try {
    const d = typeof ts === 'string' ? new Date(ts) : ts;
    return d.toLocaleString('en-US', { timeZone: 'UTC', hour12: false }) + ' UTC';
  } catch {
    return String(ts);
  }
};

const getForensicFlagChipColor = (flag: string): 'error' | 'success' | 'warning' | 'info' | 'default' => {
  if (flag.includes('High Accuracy')) return 'success';
  if (flag.includes('Moderate')) return 'warning';
  if (flag.includes('Low')) return 'error';
  if (flag.includes('Cell') || flag.includes('IP') || flag.includes('Mobile')) return 'info';
  if (flag.includes('Driving') || flag.includes('Recurring')) return 'default';
  if (flag.includes('US') || flag.includes('EU')) return 'default';
  return 'default';
};

const formatCoordinate = (coord: number) => coord?.toFixed(5) ?? 'N/A';

function getWatchlistDisplayLabel(
  registrationId: string,
  savedDeviceNames: Map<string, string>
): string {
  return savedDeviceNames.get(registrationId) ?? registrationId;
}

const DeviceListSidebar: React.FC<DeviceListSidebarProps> = ({
  tabs,
  activeTabId,
  onTabChange,
  onTabClose,
  onZoomToDeviceExtent,
  onLoadDevice,
  onLoadAllDevices,
  onSaveDevice,
  onDeviceNotes,
  onToggleDeviceVisibility,
  onFocusDevice,
  hiddenDevices,
  savedDeviceIds,
  savedDeviceNames = new Map(),
  filterByMapBounds = false,
  onFilterByMapBoundsChange,
  mapBounds,
  selectedCountries = [],
  selectedCarriers = [],
  selectedTimezones = [],
  selectedForensicFlags = [],
  onCountriesChange,
  onCarriersChange,
  onTimezonesChange,
  onForensicFlagsChange,
  showAskTracer = false,
  askTracerProps,
  showOverwatch = false,
  overwatchDataPoints = [],
  overwatchDwellLocations = [],
  onLoadArticlesToMap,
  loadedArticles = [],
  onClearArticles,
  onZoomToLocation,
  onDeleteUploadedPoint,
  onClearAllUploadedData,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const theme = useTheme();
  // Local state
  // Tab values: -2 = Overwatch, -1 = Ask Tracer, 0+ = analysis tabs
  const [internalTabIndex, setInternalTabIndex] = useState(showAskTracer ? -1 : 0);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Track which special tab is active
  const isAskTracerActive = internalTabIndex === -1;
  const isOverwatchActive = internalTabIndex === -2;

  // Auto-switch to Ask Tracer when all tabs are closed
  useEffect(() => {
    if (tabs.length === 0 && showAskTracer && internalTabIndex !== -1) {
      setInternalTabIndex(-1);
    }
  }, [tabs.length, showAskTracer, internalTabIndex]);

  // Compute active tab index (for analysis tabs only, offset by Ask Tracer)
  const activeTabIndex = useMemo(() => {
    if (isAskTracerActive) return -1;
    // If no tabs but Ask Tracer is available, force Ask Tracer
    if (tabs.length === 0 && showAskTracer) return -1;
    if (activeTabId && tabs.length > 0) {
      const idx = tabs.findIndex(t => t.id === activeTabId);
      return idx >= 0 ? idx : 0;
    }
    return internalTabIndex;
  }, [activeTabId, tabs, internalTabIndex, isAskTracerActive, showAskTracer]);

  // Compute a safe tab value that's always valid for the Tabs component
  const safeTabValue = useMemo(() => {
    // If Overwatch is active and shown, use -2
    if (isOverwatchActive && showOverwatch) return -2;
    // If Ask Tracer is active and shown, use -1
    if (isAskTracerActive && showAskTracer) return -1;
    // If there are no tabs, default to Ask Tracer if available, otherwise false (no selection)
    if (tabs.length === 0) {
      return showAskTracer ? -1 : (showOverwatch ? -2 : false);
    }
    // Ensure activeTabIndex is within bounds
    if (activeTabIndex >= 0 && activeTabIndex < tabs.length) {
      return activeTabIndex;
    }
    // Fallback to Ask Tracer if available, or first tab
    return showAskTracer ? -1 : (showOverwatch ? -2 : 0);
  }, [isAskTracerActive, isOverwatchActive, tabs.length, activeTabIndex, showAskTracer, showOverwatch]);

  // Current tab data
  const currentTab = tabs[activeTabIndex];
  const rawData = currentTab?.data || [];

  // Helper function to check if a point is within map bounds
  const isWithinBounds = useCallback((lat: number, lng: number, bounds: MapBounds | null | undefined): boolean => {
    if (!bounds) return true;
    return lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
  }, []);

  // Group devices by registration_id (with optional bounds filtering)
  const groupedDevices = useMemo(() => {
    const deviceMap = new Map<string, DeviceGroup>();
    
    // Filter data by map bounds if enabled
    const dataToProcess = filterByMapBounds && mapBounds 
      ? rawData.filter(device => isWithinBounds(device.latitude, device.longitude, mapBounds))
      : rawData;
    
    dataToProcess.forEach(device => {
      const regId = device.registration_id;
      if (!regId) return;
      
      const pointFlags = (device.all_forensic_flags && device.all_forensic_flags.length > 0)
        ? device.all_forensic_flags
        : device.forensic_flag ? [device.forensic_flag] : [];

      if (!deviceMap.has(regId)) {
        const flagsSet = new Set<string>();
        pointFlags.forEach((f: string) => flagsSet.add(f));
        deviceMap.set(regId, {
          registration_id: regId,
          pointCount: 1,
          lastPoint: device,
          latestTimestamp: device.timestamp,
          allForensicFlags: flagsSet,
        });
      } else {
        const existing = deviceMap.get(regId)!;
        existing.pointCount++;
        pointFlags.forEach((f: string) => existing.allForensicFlags.add(f));
        if (new Date(device.timestamp) > new Date(existing.latestTimestamp)) {
          existing.latestTimestamp = device.timestamp;
          existing.lastPoint = device;
        }
      }
    });

    return Array.from(deviceMap.values());
  }, [rawData, filterByMapBounds, mapBounds, isWithinBounds]);

  // Extract unique filter options from current tab data
  const filterOptions = useMemo(() => {
    const countries = new Set<string>();
    const carriers = new Set<string>();
    const timezones = new Set<string>();
    const forensicFlags = new Set<string>();
    rawData.forEach(device => {
      if (device.country) countries.add(device.country);
      if (device.carrier) carriers.add(device.carrier);
      if (device.timezone) timezones.add(device.timezone);
      if (device.all_forensic_flags && Array.isArray(device.all_forensic_flags)) {
        device.all_forensic_flags.forEach(flag => forensicFlags.add(flag));
      } else if (device.forensic_flag) {
        forensicFlags.add(device.forensic_flag);
      }
    });
    return {
      countries: Array.from(countries).sort(),
      carriers: Array.from(carriers).sort(),
      timezones: Array.from(timezones).sort(),
      forensicFlags: Array.from(forensicFlags).sort(),
    };
  }, [rawData]);

    // Filter by search term and metadata filters (Country, Carrier, Timezone, Forensic Flags)
  const filteredDevices = useMemo(() => {
    let result = groupedDevices;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(d => d.registration_id?.toLowerCase().includes(term));
    }
    if (selectedCountries.length > 0) {
      result = result.filter(d => selectedCountries.includes(d.lastPoint.country || ''));
    }
    if (selectedCarriers.length > 0) {
      result = result.filter(d => selectedCarriers.includes(d.lastPoint.carrier || ''));
    }
    if (selectedTimezones.length > 0) {
      result = result.filter(d => selectedTimezones.includes(d.lastPoint.timezone || ''));
    }
    if (selectedForensicFlags.length > 0) {
      result = result.filter(d =>
        Array.from(d.allForensicFlags || []).some(flag => selectedForensicFlags.includes(flag))
      );
    }
    return result;
  }, [groupedDevices, searchTerm, selectedCountries, selectedCarriers, selectedTimezones, selectedForensicFlags]);

  // Paginate
  const paginatedDevices = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredDevices.slice(start, start + rowsPerPage);
  }, [filteredDevices, page, rowsPerPage]);

  // All device IDs for focus feature
  const allDeviceIds = useMemo(() => 
    filteredDevices.map(d => d.registration_id), 
    [filteredDevices]
  );

  // Handlers
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    // newValue -2 = Overwatch, -1 = Ask Tracer, 0+ = analysis tabs
    if (newValue === -2) {
      setInternalTabIndex(-2);
    } else if (newValue === -1) {
      setInternalTabIndex(-1);
    } else if (onTabChange && tabs[newValue]) {
      onTabChange(tabs[newValue].id);
      setInternalTabIndex(newValue);
    } else {
      setInternalTabIndex(newValue);
    }
    setPage(0);
    setSearchTerm('');
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const activeFilterCount = selectedCountries.length + selectedCarriers.length + selectedTimezones.length + selectedForensicFlags.length;

  const handleClearFilters = () => {
    onCountriesChange?.([]);
    onCarriersChange?.([]);
    onTimezonesChange?.([]);
    onForensicFlagsChange?.([]);
    setPage(0);
  };

  const handleToggleCountry = (country: string) => {
    const updated = selectedCountries.includes(country)
      ? selectedCountries.filter(c => c !== country)
      : [...selectedCountries, country];
    onCountriesChange?.(updated);
    setPage(0);
  };

  const handleToggleCarrier = (carrier: string) => {
    const updated = selectedCarriers.includes(carrier)
      ? selectedCarriers.filter(c => c !== carrier)
      : [...selectedCarriers, carrier];
    onCarriersChange?.(updated);
    setPage(0);
  };

  const handleToggleTimezone = (timezone: string) => {
    const updated = selectedTimezones.includes(timezone)
      ? selectedTimezones.filter(t => t !== timezone)
      : [...selectedTimezones, timezone];
    onTimezonesChange?.(updated);
    setPage(0);
  };

  const handleToggleForensicFlag = (flag: string) => {
    const updated = selectedForensicFlags.includes(flag)
      ? selectedForensicFlags.filter(f => f !== flag)
      : [...selectedForensicFlags, flag];
    onForensicFlagsChange?.(updated);
    setPage(0);
  };

  const handleExportCSV = () => {
    if (!currentTab) return;

    const fullDataPoints = (currentTab as any).polDataPoints || (currentTab as any).areaDataPoints;
    const isAreaOrPol = currentTab.type === 'pol' || currentTab.type === 'area';
    const tabData = (currentTab as any).data;
    const exportRows = (isAreaOrPol && tabData && tabData.length > 0) ? tabData : fullDataPoints;
    const exploreDeviceDetails = (currentTab as any).exploreDeviceDetails as Record<string, any> | undefined;
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);

    const escapeCsv = (v: any): string => {
      if (v === undefined || v === null || v === '') return '-';
      const s = String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const formatCoord = (n: number | undefined | null) => {
      if (n === undefined || n === null || isNaN(Number(n))) return '0.000000';
      return Number(n).toFixed(6);
    };

    // Area/PoL: export all points with full columns (same as table + Last IPs)
    if (exportRows && exportRows.length > 0 && isAreaOrPol) {
      const headers = [
        'Source', 'Registration ID', 'Device Type', 'Latitude', 'Longitude', 'Timestamp', 'Accuracy (m)',
        'IP Address', 'Country', 'Carrier', 'Timezone', 'Forensic Flag',
        'Browser Family', 'Browser Version', 'OS Family', 'OS Version',
        'Device Brand', 'Device Family', 'Device Model', 'Advertiser EU',
        'Created On', 'Updated On', 'User Agent Full', 'Last IPs'
      ];
      const csvRows = [headers.join(',')];

      exportRows.forEach((row: any) => {
        const regId = row.registration_id ?? row.studentId ?? row.deviceId ?? '';
        const regIdNorm = String(regId).replace(/^GN-/i, '');
        const advEu = row.advertiser_eu === true ? 'Yes' : row.advertiser_eu === false ? 'No' : '-';
        const forensicFlag = (row.all_forensic_flags && row.all_forensic_flags.length > 0)
          ? row.all_forensic_flags.join('; ')
          : (row.forensic_flag ?? row.metadata?.forensic_flag ?? '-');
        const lastIpsEntry = regIdNorm && exploreDeviceDetails?.[regIdNorm]?.lastSeenIPAddresses;
        const lastIpsStr = Array.isArray(lastIpsEntry) && lastIpsEntry.length > 0
          ? lastIpsEntry.slice(0, 50).map((e: any) =>
              e.timestamp != null
                ? `${e.ipAddress ?? '–'} (${formatTimestampCSV(new Date(e.timestamp).toISOString())})`
                : (e.ipAddress ?? '–')
            ).join('; ')
          : '-';
        const rowTs = row.timestamp instanceof Date ? row.timestamp.toISOString() : row.timestamp;
        const lat = row.latitude ?? row.coordinates?.[0];
        const lng = row.longitude ?? row.coordinates?.[1];

        csvRows.push([
          escapeCsv(row.source || 'OSINT API'),
          escapeCsv(regId),
          escapeCsv(row.deviceType || 'phone'),
          formatCoord(lat),
          formatCoord(lng),
          `"${formatTimestampCSV(rowTs)}"`,
          row.accuracy != null ? Math.round(Number(row.accuracy)) : '-',
          escapeCsv(row.ip_address ?? row.metadata?.ip_address),
          escapeCsv(row.country ?? row.metadata?.country),
          escapeCsv(row.carrier ?? row.metadata?.carrier),
          escapeCsv(row.timezone ?? row.metadata?.timezone),
          escapeCsv(forensicFlag),
          escapeCsv(row.browser_family ?? row.metadata?.browser_family),
          escapeCsv(row.browser_version ?? row.metadata?.browser_version),
          escapeCsv(row.os_family ?? row.metadata?.os_family),
          escapeCsv(row.os_version ?? row.metadata?.os_version),
          escapeCsv(row.device_brand ?? row.metadata?.device_brand),
          escapeCsv(row.device_family ?? row.metadata?.device_family),
          escapeCsv(row.device_model ?? row.metadata?.device_model),
          advEu,
          row.created_on ? `"${formatTimestampCSV(row.created_on)}"` : '-',
          row.updated_on ? `"${formatTimestampCSV(row.updated_on)}"` : '-',
          escapeCsv(row.user_agent_full ?? row.metadata?.user_agent_full),
          escapeCsv(lastIpsStr)
        ].join(','));
      });

      const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${(currentTab.label || 'export').replace(/\s+/g, '_')}_full_path_${ts}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    // Grouped summary (one row per device) with full columns + Last IPs
    if (filteredDevices.length === 0) return;
    const headers = [
      'Source', 'Registration ID', 'Device Type', 'Point Count',
      'Latitude', 'Longitude', 'Timestamp', 'Accuracy (m)',
      'IP Address', 'Country', 'Carrier', 'Timezone', 'Forensic Flag',
      'Browser Family', 'Browser Version', 'OS Family', 'OS Version',
      'Device Brand', 'Device Family', 'Device Model', 'Advertiser EU',
      'Created On', 'Updated On', 'User Agent Full', 'Last IPs'
    ];
    const csvRows = [headers.join(',')];

    filteredDevices.forEach(d => {
      const p = d.lastPoint;
      const regIdNorm = String(d.registration_id || '').replace(/^GN-/i, '');
      const advEu = p.advertiser_eu === true ? 'Yes' : p.advertiser_eu === false ? 'No' : '-';
      const forensicFlag = d.allForensicFlags?.size
        ? Array.from(d.allForensicFlags).join('; ')
        : (p.forensic_flag ?? (p.all_forensic_flags && p.all_forensic_flags.join?.('; ')) ?? '-');
      const lastIpsEntry = regIdNorm && exploreDeviceDetails?.[regIdNorm]?.lastSeenIPAddresses;
      const lastIpsStr = Array.isArray(lastIpsEntry) && lastIpsEntry.length > 0
        ? lastIpsEntry.slice(0, 50).map((e: any) =>
            e.timestamp != null
              ? `${e.ipAddress ?? '–'} (${formatTimestampCSV(new Date(e.timestamp).toISOString())})`
              : (e.ipAddress ?? '–')
          ).join('; ')
        : '-';

      csvRows.push([
        escapeCsv(p.source || 'Tracer API'),
        escapeCsv(d.registration_id),
        escapeCsv(p.deviceType || 'phone'),
        d.pointCount,
        formatCoord(p.latitude),
        formatCoord(p.longitude),
        `"${formatTimestampCSV(d.latestTimestamp)}"`,
        p.accuracy != null ? Math.round(Number(p.accuracy)) : '-',
        escapeCsv(p.ip_address),
        escapeCsv(p.country),
        escapeCsv(p.carrier),
        escapeCsv(p.timezone),
        escapeCsv(forensicFlag),
        escapeCsv(p.browser_family),
        escapeCsv(p.browser_version),
        escapeCsv(p.os_family),
        escapeCsv(p.os_version),
        escapeCsv(p.device_brand),
        escapeCsv(p.device_family),
        escapeCsv(p.device_model),
        advEu,
        p.created_on ? `"${formatTimestampCSV(p.created_on)}"` : '-',
        p.updated_on ? `"${formatTimestampCSV(p.updated_on)}"` : '-',
        escapeCsv(p.user_agent_full),
        escapeCsv(lastIpsStr)
      ].join(','));
    });

    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `devices_${(currentTab.label || 'export').replace(/\s+/g, '_')}_${ts}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Show Ask Tracer even when no tabs
  if (tabs.length === 0 && !showAskTracer) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No data available. Perform an area search to see devices.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Tab bar */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Tabs
          value={safeTabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              textTransform: 'none',
              fontSize: '0.8rem',
              px: 1.5,
            },
          }}
        >
          {/* Ask Tracer Tab - always first */}
          {showAskTracer && (
            <Tab
              value={-1}
              icon={<TracerIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label="Ask Sentinel"
              sx={{ minHeight: 36 }}
            />
          )}
          {/* Overwatch Tab */}
          {showOverwatch && (
            <Tab
              value={-2}
              icon={<OverwatchIcon sx={{ fontSize: 16, color: loadedArticles.length > 0 ? '#ff9800' : undefined }} />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Overwatch
                  {loadedArticles.length > 0 && (
                    <Chip
                      label={loadedArticles.length}
                      size="small"
                      sx={{ 
                        height: 16, 
                        fontSize: '0.6rem', 
                        bgcolor: '#ff9800', 
                        color: 'white',
                        '& .MuiChip-label': { px: 0.75 }
                      }}
                    />
                  )}
                </Box>
              }
              sx={{ minHeight: 36 }}
            />
          )}
          {/* Analysis Tabs */}
          {tabs.map((tab, index) => (
            <Tab
              key={tab.id}
              value={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{tab.label}</span>
                  <Chip label={tab.totalDevices} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                  {onTabClose && (
                    <Box
                      component="span"
                      role="button"
                      tabIndex={0}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        onTabClose(tab.id);
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          e.preventDefault();
                          onTabClose(tab.id);
                        }
                      }}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 0.25,
                        ml: 0.5,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        '&:focus': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </Box>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>
      
      {/* Ask Tracer Content */}
      {isAskTracerActive && showAskTracer && askTracerProps && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <AskTracer
            dataPoints={askTracerProps.dataPoints}
            dwellLocations={askTracerProps.dwellLocations}
            onZoomToLocation={askTracerProps.onZoomToLocation}
            isFullPanel={true}
          />
        </Box>
      )}

      {/* Overwatch Content */}
      {isOverwatchActive && showOverwatch && (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <OverwatchIntel
            dataPoints={overwatchDataPoints}
            dwellLocations={overwatchDwellLocations}
            onLoadArticlesToMap={onLoadArticlesToMap}
            loadedArticles={loadedArticles}
            onClearArticles={onClearArticles}
            onZoomToLocation={onZoomToLocation}
            isFullPanel
          />
        </Box>
      )}
      
      {/* Device content - only show when not on Ask Tracer or Overwatch tab */}
      {!isAskTracerActive && !isOverwatchActive && tabs.length > 0 && (
        <>

      {/* Search and actions bar */}
      <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by ID..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          {/* Filter by Country / Carrier / Timezone / Forensic Flags */}
          {(onCountriesChange || onCarriersChange || onTimezonesChange || onForensicFlagsChange) && (
            <Tooltip title="Filter by Country, Carrier, Timezone, or Forensic Flags">
              <Badge badgeContent={activeFilterCount > 0 ? activeFilterCount : 0} color="primary">
                <IconButton
                  size="small"
                  onClick={() => setFilterDialogOpen(true)}
                  sx={{
                    bgcolor: activeFilterCount > 0 ? theme.palette.primary.main : `${theme.palette.primary.main}1A`,
                    color: activeFilterCount > 0 ? 'white' : 'primary.main',
                    border: '1.5px solid',
                    borderColor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: 'white',
                    },
                  }}
                >
                  <FilterListIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Badge>
            </Tooltip>
          )}
          {/* Filter by Map View Toggle */}
          {onFilterByMapBoundsChange && (
            <Tooltip title={filterByMapBounds ? "Filter by Map View: ON - Showing only devices in visible map area" : "Filter by Map View: OFF - Showing all devices"}>
              <IconButton
                size="small"
                onClick={() => onFilterByMapBoundsChange(!filterByMapBounds)}
                sx={{ 
                  bgcolor: filterByMapBounds 
                    ? theme.palette.info.main 
                    : `${theme.palette.info.main}1A`,
                  color: filterByMapBounds 
                    ? 'white' 
                    : 'info.main',
                  border: '1.5px solid',
                  borderColor: 'info.main',
                  '&:hover': { 
                    bgcolor: filterByMapBounds 
                      ? theme.palette.info.dark 
                      : 'info.main',
                    color: 'white',
                  } 
                }}
              >
                <CountryIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {onLoadAllDevices && (
            <Tooltip title="Load all devices Pattern of Life">
              <IconButton
                size="small"
                onClick={() => onLoadAllDevices(allDeviceIds)}
                sx={{ 
                  bgcolor: `${theme.palette.secondary.main}1A`,
                  color: 'secondary.main',
                  border: '1.5px solid',
                  borderColor: 'secondary.main',
                  '&:hover': { 
                    bgcolor: 'secondary.main',
                    color: 'white',
                  } 
                }}
              >
                <LoadAllIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Export to CSV">
            <IconButton
              size="small"
              onClick={handleExportCSV}
              disabled={filteredDevices.length === 0}
              sx={{ 
                bgcolor: `${theme.palette.primary.main}1A`,
                color: 'primary.main',
                border: '1.5px solid',
                borderColor: 'primary.main',
                '&:hover': { 
                  bgcolor: 'primary.main',
                  color: 'white',
                },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  borderColor: 'action.disabled',
                  color: 'text.disabled',
                }
              }}
            >
              <DownloadIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {/* Clear All button for uploaded data */}
          {currentTab?.type === 'csv' && onClearAllUploadedData && (
            <Tooltip title="Clear all uploaded data" disableInteractive>
              <IconButton
                size="small"
                disableRipple
                onClick={() => {
                  if (window.confirm('Are you sure you want to remove all uploaded/searched data?')) {
                    onClearAllUploadedData();
                  }
                }}
                disabled={filteredDevices.length === 0}
                sx={{ 
                  bgcolor: 'error.main',
                  color: 'white',
                  width: 32,
                  height: 32,
                  transition: 'none',
                  '&:hover': { 
                    bgcolor: 'error.dark',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'text.disabled',
                  }
                }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredDevices.length}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Per page:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
        sx={{
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiToolbar-root': { minHeight: 40, pl: 1, pr: 1 },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: '0.75rem',
          },
        }}
      />

      {/* Device list - scrollable area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1, pb: 8 }}>
        {paginatedDevices.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchTerm ? 'No devices match your search.' : 'No devices available.'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ pb: 6 }}>
            {paginatedDevices.map((device) => {
              const isHidden = hiddenDevices.has(device.registration_id);
              const isSaved = savedDeviceIds.has(device.registration_id);
              
              return (
                <Card
                  key={device.registration_id}
                  sx={{
                    mb: 1,
                    opacity: isHidden ? 0.5 : 1,
                    filter: isHidden ? 'grayscale(50%)' : 'none',
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Header: ID + badges */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
                      <Box sx={{ pt: 0.15 }}>
                        {isSaved ? (
                          <StarIcon sx={{ fontSize: 16, color: '#FFD700' }} />
                        ) : (
                          <DeviceIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: isSaved ? 600 : 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.2,
                          }}
                          title={
                            getWatchlistDisplayLabel(device.registration_id, savedDeviceNames) ===
                            device.registration_id
                              ? device.registration_id
                              : `${getWatchlistDisplayLabel(device.registration_id, savedDeviceNames)}\n${device.registration_id}`
                          }
                        >
                          {getWatchlistDisplayLabel(device.registration_id, savedDeviceNames)}
                        </Typography>
                        {savedDeviceNames.get(device.registration_id) &&
                          getWatchlistDisplayLabel(device.registration_id, savedDeviceNames) !==
                            device.registration_id && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.65rem',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {device.registration_id}
                            </Typography>
                          )}
                      </Box>
                      {device.lastPoint.country && (
                        <Chip
                          icon={<CountryIcon sx={{ fontSize: 12 }} />}
                          label={device.lastPoint.country}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }}
                        />
                      )}
                      <Chip
                        label={`${device.pointCount} pts`}
                        size="small"
                        color="primary"
                        sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }}
                      />
                    </Box>

                    {/* Location */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                      <LocationIcon sx={{ fontSize: 14, color: generateDeviceColor(device.registration_id), mt: 0.2 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                          {formatCoordinate(device.lastPoint.latitude)}, {formatCoordinate(device.lastPoint.longitude)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Timestamp */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <TimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(device.latestTimestamp)}
                      </Typography>
                    </Box>

                    {/* Forensic flags (only when present) */}
                    {(() => {
                      const lp = device.lastPoint;
                      const allFlags = device.allForensicFlags?.size
                        ? Array.from(device.allForensicFlags)
                        : (lp.all_forensic_flags && lp.all_forensic_flags.length > 0)
                          ? lp.all_forensic_flags
                          : lp.forensic_flag ? [lp.forensic_flag] : [];
                      if (allFlags.length === 0) return null;
                      const primaryFlag = allFlags[0] ?? lp.forensic_flag ?? '';
                      const hasMultiple = allFlags.length > 1;
                      return (
                        <Tooltip
                          title={
                            <Box component="span" sx={{ display: 'block', maxHeight: 240, overflow: 'auto', color: '#1a1a1a' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'inherit' }}>
                                All forensic flags:
                              </Typography>
                              {allFlags.map((flag, idx) => (
                                <Typography key={idx} variant="caption" sx={{ display: 'block', mt: 0.3, color: 'inherit' }}>
                                  • {flag}
                                </Typography>
                              ))}
                            </Box>
                          }
                          placement="top"
                          enterDelay={200}
                          arrow
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                            <FlagIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Chip
                              label={primaryFlag}
                              size="small"
                              color={getForensicFlagChipColor(primaryFlag)}
                              sx={{ height: 20, fontSize: '0.7rem', maxWidth: '100%' }}
                            />
                            {hasMultiple && (
                              <Chip
                                label={`+${allFlags.length - 1}`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem', minWidth: 32, '& .MuiChip-label': { px: 0.5 } }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })()}

                    {/* Action buttons */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {onZoomToDeviceExtent && (
                        <IconButton
                          size="small"
                          title="Zoom to extent"
                          onClick={() => {
                            const points = rawData.filter(d => d.registration_id === device.registration_id);
                            onZoomToDeviceExtent(device.registration_id, points);
                          }}
                          sx={{ 
                            bgcolor: `${theme.palette.primary.main}1A`,
                            color: 'primary.main',
                            border: '1.5px solid',
                            borderColor: 'primary.main',
                            width: 26, 
                            height: 26, 
                            '&:hover': { 
                              bgcolor: 'primary.main',
                              color: 'white',
                            } 
                          }}
                        >
                          <ZoomExtentIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                      {onToggleDeviceVisibility && (
                        <IconButton
                          size="small"
                          title={isHidden ? 'Show' : 'Hide'}
                          onClick={() => onToggleDeviceVisibility(device.registration_id)}
                          sx={{ 
                            bgcolor: isHidden ? 'action.disabled' : `${theme.palette.primary.main}1A`,
                            color: isHidden ? 'text.disabled' : 'primary.main',
                            border: '1.5px solid',
                            borderColor: isHidden ? 'action.disabled' : 'primary.main',
                            width: 26, 
                            height: 26, 
                            '&:hover': { 
                              bgcolor: isHidden ? 'action.selected' : 'primary.main',
                              color: isHidden ? 'text.primary' : 'white',
                            } 
                          }}
                        >
                          {isHidden ? <VisibilityOff sx={{ fontSize: 14 }} /> : <Visibility sx={{ fontSize: 14 }} />}
                        </IconButton>
                      )}
                      {onFocusDevice && (
                        <IconButton
                          size="small"
                          title="Focus on this device"
                          onClick={() => onFocusDevice(device.registration_id, allDeviceIds)}
                          sx={{ 
                            bgcolor: `${theme.palette.primary.main}1A`,
                            color: 'primary.main',
                            border: '1.5px solid',
                            borderColor: 'primary.main',
                            width: 26, 
                            height: 26, 
                            '&:hover': { 
                              bgcolor: 'primary.main',
                              color: 'white',
                            } 
                          }}
                        >
                          <FocusIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                      {/* Hide Load PoL, Notes, and Save buttons for uploaded/CSV data */}
                      {onLoadDevice && currentTab?.type !== 'csv' && (
                        <IconButton
                          size="small"
                          title="Load Pattern of Life"
                          onClick={() => onLoadDevice(device.registration_id)}
                          sx={{ 
                            bgcolor: `${theme.palette.secondary.main}1A`,
                            color: 'secondary.main',
                            border: '1.5px solid',
                            borderColor: 'secondary.main',
                            width: 26, 
                            height: 26, 
                            '&:hover': { 
                              bgcolor: 'secondary.main',
                              color: 'white',
                            } 
                          }}
                        >
                          <LoadIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                      {onDeviceNotes && currentTab?.type !== 'csv' && (
                        <IconButton
                          size="small"
                          title="Notes"
                          onClick={() => onDeviceNotes(device.registration_id)}
                          sx={{ 
                            bgcolor: `${theme.palette.warning.main}1A`,
                            color: 'warning.main',
                            border: '1.5px solid',
                            borderColor: 'warning.main',
                            width: 26, 
                            height: 26, 
                            '&:hover': { 
                              bgcolor: 'warning.main',
                              color: 'white',
                            } 
                          }}
                        >
                          <NoteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                      {onSaveDevice && !isSaved && currentTab?.type !== 'csv' && (
                        <IconButton
                          size="small"
                          title="Save to Watchlist"
                          onClick={() => onSaveDevice(device.registration_id)}
                          sx={{ bgcolor: 'grey.600', color: 'white', width: 26, height: 26, '&:hover': { bgcolor: 'grey.700' } }}
                        >
                          <StarIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                      {/* Delete button for uploaded/CSV data */}
                      {currentTab?.type === 'csv' && onDeleteUploadedPoint && (
                        <IconButton
                          size="small"
                          title="Remove this point"
                          disableRipple
                          onClick={() => {
                            const pointId = (device as any).id || device.registration_id;
                            onDeleteUploadedPoint(pointId);
                          }}
                          sx={{ 
                            bgcolor: 'error.main', 
                            color: 'white', 
                            width: 26, 
                            height: 26,
                            transition: 'none',
                            '&:hover': { bgcolor: 'error.dark' } 
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </List>
        )}
      </Box>
      {/* Filter Dialog - same filters as Data Table */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            <Typography variant="h6">Filter Devices</Typography>
            {activeFilterCount > 0 && (
              <Chip label={`${activeFilterCount} active`} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
          </Box>
          <IconButton edge="end" onClick={() => setFilterDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Country ({filterOptions.countries.length} available)
            </Typography>
            {filterOptions.countries.length > 0 ? (
              <FormGroup>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {filterOptions.countries.map(country => (
                    <FormControlLabel
                      key={country}
                      control={<Checkbox checked={selectedCountries.includes(country)} onChange={() => handleToggleCountry(country)} size="small" />}
                      label={<Typography variant="body2">{country}</Typography>}
                      sx={{ m: 0, mr: 2 }}
                    />
                  ))}
                </Box>
              </FormGroup>
            ) : (
              <Typography variant="body2" color="text.secondary">No countries found in data</Typography>
            )}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Carrier ({filterOptions.carriers.length} available)
            </Typography>
            {filterOptions.carriers.length > 0 ? (
              <FormGroup>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {filterOptions.carriers.map(carrier => (
                    <FormControlLabel
                      key={carrier}
                      control={<Checkbox checked={selectedCarriers.includes(carrier)} onChange={() => handleToggleCarrier(carrier)} size="small" />}
                      label={<Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{carrier}</Typography>}
                      sx={{ m: 0, mr: 1, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </FormGroup>
            ) : (
              <Typography variant="body2" color="text.secondary">No carriers found in data</Typography>
            )}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Timezone ({filterOptions.timezones.length} available)
            </Typography>
            {filterOptions.timezones.length > 0 ? (
              <FormGroup>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {filterOptions.timezones.map(timezone => (
                    <FormControlLabel
                      key={timezone}
                      control={<Checkbox checked={selectedTimezones.includes(timezone)} onChange={() => handleToggleTimezone(timezone)} size="small" />}
                      label={<Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{timezone}</Typography>}
                      sx={{ m: 0, mr: 1, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </FormGroup>
            ) : (
              <Typography variant="body2" color="text.secondary">No timezones found in data</Typography>
            )}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Forensic Flags ({filterOptions.forensicFlags.length} available)
            </Typography>
            {filterOptions.forensicFlags.length > 0 ? (
              <FormGroup>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {filterOptions.forensicFlags.map(flag => (
                    <FormControlLabel
                      key={flag}
                      control={<Checkbox checked={selectedForensicFlags.includes(flag)} onChange={() => handleToggleForensicFlag(flag)} size="small" />}
                      label={
                        <Chip
                          label={flag}
                          size="small"
                          color={
                            flag.includes('High') || flag.includes('Accurate') ? 'success' :
                            flag.includes('Low') || flag.includes('Stale') ? 'error' :
                            flag.includes('Wi-Fi') || flag.includes('WiFi') ? 'warning' :
                            flag.includes('GPS') ? 'success' : flag.includes('Mobile') ? 'info' : 'default'
                          }
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      }
                      sx={{ m: 0, mr: 1, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </FormGroup>
            ) : (
              <Typography variant="body2" color="text.secondary">No forensic flags found in data</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearFilters} disabled={activeFilterCount === 0} color="warning">
            Clear All
          </Button>
          <Button onClick={() => setFilterDialogOpen(false)} variant="contained">
            Apply Filters ({filteredDevices.length} devices)
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    </Box>
  );
};

export default DeviceListSidebar;

